import React, { useState, useCallback } from "react";
import Layout from "../../components/Common/Layout";
import { investment } from "../../types/pages/Team/detail";
import { AddNumberDelimiter } from "../../utils/TextUtil";

type BankInfo = {
  bank_name: string;
  branch_name: string;
  account_number: string;
  account_name: string;
};

type Props = {
  investment: investment;
  prefecture: string;
  main_image: string | null;
  bank_info: BankInfo;
};

const PRESET_AMOUNTS = [3000, 5000, 10000, 30000, 50000];
const MIN_AMOUNT = 1000;

const calculateSystemFee = (_amount: number): number => {
  return 220;
};

const DirectFundingIndex: React.FC<Props> = (props) => {
  const [selectedPreset, setSelectedPreset] = useState<number | null>(3000);
  const [customInput, setCustomInput] = useState<string>("");
  const [isCustomMode, setIsCustomMode] = useState(false);

  const currentAmount = isCustomMode
    ? parseInt(customInput, 10) || 0
    : selectedPreset ?? 0;

  const systemFee = currentAmount >= MIN_AMOUNT ? calculateSystemFee(currentAmount) : 0;
  const supportAmount = currentAmount - systemFee;

  const handlePresetSelect = useCallback((amount: number) => {
    setSelectedPreset(amount);
    setIsCustomMode(false);
    setCustomInput("");
  }, []);

  const handleCustomMode = useCallback(() => {
    setIsCustomMode(true);
    setSelectedPreset(null);
  }, []);

  const handleCustomInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setCustomInput(value);
  }, []);

  const isValidAmount = currentAmount >= MIN_AMOUNT;

  return (
    <Layout
      breadcrumbs={[
        { text: "HOME", href: "/" },
        {
          text: props.investment.title,
          href: `/${props.prefecture}/team/${props.investment.client_id}/invest/${props.investment.id}/detail`,
        },
        { text: "直接口座振込で支援する" },
      ]}
    >
      <section className="px-[30px] mt-5 mb-10 sm:max-w-main sm:mb-[40px] sm:mx-auto md:px-0 sm:mt-[60px]">
        <div className="sm:w-[700px] sm:mx-auto">
          {/* ヘッダー: プロジェクト情報 */}
          <div className="flex mb-5 sm:mb-[35px]">
            {props.main_image && (
              <div className="w-[80px] h-[60px] mr-[10px] sm:w-[120px] sm:h-[90px] sm:mr-5">
                <img
                  src={props.main_image}
                  alt={props.investment.title}
                  className="object-contain w-full h-full"
                />
              </div>
            )}
            <h2 className="sm:w-[calc(100%-140px)] w-[calc(100%-90px)] text-sm underline">
              <a
                className="link-hover-none"
                href={`/${props.prefecture}/team/${props.investment.client_id}/invest/${props.investment.id}/detail`}
              >
                {props.investment.title}
              </a>
            </h2>
          </div>

          {/* 振込案内ボックス */}
          <div className="border border-[#D2DFE6] rounded p-5 mb-8">
            <p className="font-bold text-center mb-2">
              こちらのプロジェクトには、振込専用口座をご用意しております
            </p>
            <p className="text-sm text-[#768084]">
              <span className="font-bold text-primary">振込専用口座からのご支援のリターンは、お礼のメッセージです。</span>皆さまへのお礼のメッセージは、プロジェクト終了後の「コミュニティ」で公開されます。
            </p>
          </div>

          {/* ① 振込金額を検討する */}
          <section className="mb-8">
            <h3 className="text-base font-bold mb-3">① 振り込み金額を検討する</h3>
            <p className="text-sm text-[#768084] mb-1">
              {MIN_AMOUNT.toLocaleString()}円以上の金額をお振り込みください。
            </p>
            <p className="text-sm text-[#768084] mb-5">
              振り込み金額からプラットフォーム利用料が差し引かれた金額が、プロジェクトへの支援金額となります。
            </p>

            {/* プリセット金額ボタン */}
            <div className="flex flex-wrap gap-3 mb-5">
              {PRESET_AMOUNTS.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => handlePresetSelect(amount)}
                  className={`px-4 py-2 border rounded text-sm font-medium transition-colors ${
                    !isCustomMode && selectedPreset === amount
                      ? "bg-[#006AC9] text-white border-[#006AC9]"
                      : "bg-white text-[#333] border-[#D2DFE6] hover:border-[#006AC9]"
                  }`}
                >
                  {amount.toLocaleString()}円
                </button>
              ))}
              <button
                type="button"
                onClick={handleCustomMode}
                className={`px-4 py-2 border rounded text-sm font-medium transition-colors ${
                  isCustomMode
                    ? "bg-[#006AC9] text-white border-[#006AC9]"
                    : "bg-white text-[#333] border-[#D2DFE6] hover:border-[#006AC9]"
                }`}
              >
                直接入力する
              </button>
            </div>

            {/* カスタム金額入力 */}
            {isCustomMode && (
              <div className="mb-5">
                <div className="flex items-center border border-[#D2DFE6] rounded overflow-hidden w-full sm:w-[240px]">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={AddNumberDelimiter(customInput)}
                    onChange={handleCustomInputChange}
                    placeholder="金額を入力"
                    className="flex-1 px-3 py-2 text-right outline-none text-sm"
                  />
                  <span className="px-3 py-2 bg-[#F4F6F8] text-sm text-[#768084]">円</span>
                </div>
                {customInput && !isValidAmount && (
                  <p className="text-red-500 text-xs mt-1">
                    {MIN_AMOUNT.toLocaleString()}円以上の金額を入力してください。
                  </p>
                )}
              </div>
            )}

            {/* 金額内訳 */}
            <div className="border border-[#D2DFE6] rounded p-4 mb-4">
              <dl className="pt-0 pb-3 border-b border-[#D2DFE6]">
                <div className="flex justify-between items-center mb-1">
                  <dt className="text-sm text-[#768084]">振込金額</dt>
                  <dd className="text-3xl font-bold">
                    {isValidAmount ? currentAmount.toLocaleString() : "—"} <span className="text-base font-normal">円</span>
                  </dd>
                </div>
              </dl>
              <dl className="py-3 border-b border-[#D2DFE6]">
                <div className="flex justify-between items-center mb-1">
                  <dt className="text-sm text-[#768084]">プラットフォーム利用料（税込）</dt>
                  <dd className="text-sm text-[#768084]">
                    {isValidAmount ? `-${systemFee.toLocaleString()}` : "—"} <span className="text-xs text-[#768084]">円</span>
                  </dd>
                </div>
                <p className="text-xs text-[#768084]">支援金額に対していただいております。</p>
              </dl>
              <dl className="flex justify-between items-center py-4">
                <dt className="text-sm text-[#768084]">プロジェクトへの支援金額</dt>
                <dd className="text-sm text-[#768084]">
                  {isValidAmount && supportAmount > 0 ? supportAmount.toLocaleString() : "—"} <span className="text-xs text-[#768084]">円</span>
                </dd>
              </dl>
            </div>

            {/* プラットフォーム利用料の注意書き */}
            <div className="bg-[#FFF9E6] border border-[#F0D080] rounded p-4 flex gap-2">
              <span className="text-[#C8960C] flex-shrink-0">ⓘ</span>
              <div>
                <p className="text-sm font-bold text-[#7A5C00] mb-1 ml-1">プラットフォーム利用料について</p>
                <p className="text-sm text-[#7A5C00] ml-1">
                  安心・安全にご支援いただくために、1回のご支援ごとに支援者様にプラットフォーム利用料をいただいております。
                </p>
              </div>
            </div>
          </section>

          {/* ② 口座情報 */}
          <section className="mb-10">
            <h3 className="text-base font-bold mb-4">② 以下の口座へ振り込む</h3>
            <table className="w-full border-collapse border border-[#D2DFE6] text-sm">
              <tbody>
                <tr className="border-b border-[#D2DFE6]">
                  <th className="text-left bg-[#F4F6F8] px-4 py-3 w-[35%] font-medium text-[#768084]">
                    銀行名
                  </th>
                  <td className="px-4 py-3">{props.bank_info.bank_name}</td>
                </tr>
                <tr className="border-b border-[#D2DFE6]">
                  <th className="text-left bg-[#F4F6F8] px-4 py-3 font-medium text-[#768084]">
                    支店名
                  </th>
                  <td className="px-4 py-3">{props.bank_info.branch_name}</td>
                </tr>
                <tr className="border-b border-[#D2DFE6]">
                  <th className="text-left bg-[#F4F6F8] px-4 py-3 font-medium text-[#768084]">
                    口座番号
                  </th>
                  <td className="px-4 py-3">{props.bank_info.account_number}</td>
                </tr>
                <tr>
                  <th className="text-left bg-[#F4F6F8] px-4 py-3 font-medium text-[#768084]">
                    口座名義
                  </th>
                  <td className="px-4 py-3">{props.bank_info.account_name}</td>
                </tr>
              </tbody>
            </table>

            <div className="mt-4 p-4 bg-[#F4F6F8] rounded text-sm text-[#768084]">
              <ul className="space-y-1 list-disc pl-4">
                <li>振込手数料は支援者様のご負担となります。</li>
                <li>お振込金額の過不足が生じた場合、実際のお振込金額を正としてして支援お申し込みを受け付けます。</li>
                <li>
                  意図された金額と異なる金額をお振り込みされた方は、ご自身の金融機関へと組戻し等の処理をお願いいたします。
                </li>
              </ul>
            </div>
          </section>

          {/* プロジェクト詳細ページに戻る */}
          <div className="mt-[60px] text-center">
            <a
              href={`/${props.prefecture}/team/${props.investment.client_id}/invest/${props.investment.id}/detail`}
              className="left-icon icon-stick-arrow-left font-medium text-sm"
            >
              プロジェクトの詳細ページに戻る
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DirectFundingIndex;
